import type { DataCollectionRequest } from './data.service';

export interface TableDataHeader {
  field: string;
  name: string;
}

export interface TableHeaderLabels {
  date: string;
  pv: string;
  pvIframe: string;
  pvInMap: string;
  pvInMapIframe: string;
  user: string;
  userIframe: string;
  userInMap: string;
  userInMapIframe: string;
  session: string;
  sessionIframe: string;
  sessionInMapIframe: string;
  sessionInMap: string;
  spotName: string;
  spotClick: string;
  spotClickInMap: string;
  referrer: string;
  campaignName: string;
  campaignSource: string;
  campaignMedium: string;
  language: string;
  device: string;
  area: string;
  os: string;
}

/**
 * 指定されたコレクションに対応するテーブルヘッダーを取得します。
 * @param collection データコレクションのリクエスト情報
 * @param labels 翻訳済みラベル辞書
 * @returns テーブルヘッダーの配列
 */
export function getTableHeaders(
  collection: DataCollectionRequest,
  labels: TableHeaderLabels,
): TableDataHeader[] {
  let headers: TableDataHeader[] = [];
  const { name, isDaily } = collection;

  switch (name) {
    // type 1: 日ごとの集計
    case 'v1miaccess':
      // 重複がありますが仕様です
      headers = [
        { field: 'date', name: labels.date },
        { field: 'pv', name: labels.pv }, // ページ閲覧数
        { field: 'pv_iframe', name: labels.pvIframe }, // 埋込マップ閲覧数
        { field: 'uu', name: labels.user }, // ユーザー数
        { field: 'uu_iframe', name: labels.userIframe }, // 埋込マップユーザー数
        { field: 'session', name: labels.session }, // セッション数
        { field: 'session_iframe', name: labels.sessionIframe }, // 埋込マップセッション数
        { field: 'inmap_pv', name: labels.pvInMap }, // ページ閲覧数（位置情報ON利用者）
        { field: 'inmap_uu_iframe', name: labels.userInMapIframe }, // 埋込マップユーザー数（位置情報ON利用者）
        {
          field: 'inmap_session_iframe',
          name: labels.sessionInMapIframe,
        }, // 埋込マップセッション数（位置情報ON利用者）
        { field: 'inmap_uu', name: labels.userInMap }, // 位置情報ON利用者数
        { field: 'inmap_uu_iframe_2', name: labels.userInMapIframe }, // 埋込マップユーザー数（位置情報ON利用者）- 重複
        { field: 'inmap_session', name: labels.sessionInMap }, // 位置情報ONセッション数
        {
          field: 'inmap_session_iframe_2',
          name: labels.sessionInMapIframe,
        }, // 埋込マップセッション数（位置情報ON利用者）- 重複
      ];
      break;

    case 'v1landmark':
      if (isDaily) {
        headers = [
          { field: 'date', name: labels.date },
          { field: 'pv', name: labels.pv },
          { field: 'uu', name: labels.user },
          { field: 'pv_iframe', name: labels.pvIframe },
          { field: 'uu_iframe', name: labels.userIframe },
          { field: 'inmap_pv', name: labels.pvInMap },
          { field: 'inmap_uu', name: labels.userInMap },
          { field: 'inmap_pv_iframe', name: labels.pvInMapIframe },
          {
            field: 'inmap_uu_iframe',
            name: labels.userInMapIframe,
          },
        ];
      } else {
        // 期間中の総数の集計
        headers = [
          { field: 'name', name: labels.spotName },
          { field: 'click', name: labels.spotClick },
        ];
      }
      break;

    // type 2: 期間中の総数の集計
    case 'v1mireferrer':
      headers = [
        { field: 'name', name: labels.referrer },
        { field: 'pv', name: labels.pv },
        { field: 'pv_iframe', name: labels.pvIframe },
        { field: 'uu', name: labels.user },
        { field: 'uu_iframe', name: labels.userIframe },
      ];
      break;

    case 'v2micampaign':
      headers = [
        { field: 'cp', name: labels.campaignName },
        { field: 'md', name: labels.campaignMedium },
        { field: 'sc', name: labels.campaignSource },
        { field: 'pv', name: labels.pv },
        { field: 'uu', name: labels.user },
      ];
      break;

    case 'v2spots':
      headers = [
        { field: 'name', name: labels.spotName },
        { field: 'click', name: labels.spotClick },
        { field: 'inmap_click', name: labels.spotClickInMap },
      ];
      break;

    case 'v1milang':
      headers = [
        { field: 'name', name: labels.language },
        { field: 'pv', name: labels.pv },
        { field: 'pv_iframe', name: labels.pvIframe },
        { field: 'uu', name: labels.user },
        { field: 'uu_iframe', name: labels.userIframe },
      ];
      break;

    case 'v1miarea':
      headers = [
        { field: 'name', name: labels.area },
        { field: 'pv', name: labels.pv },
        { field: 'pv_iframe', name: labels.pvIframe },
        { field: 'uu', name: labels.user },
        { field: 'uu_iframe', name: labels.userIframe },
      ];
      break;

    case 'v1model':
      headers = [
        { field: 'name', name: labels.device },
        { field: 'pv', name: labels.pv },
        { field: 'pv_iframe', name: labels.pvIframe },
        { field: 'uu', name: labels.user },
        { field: 'uu_iframe', name: labels.userIframe },
      ];
      break;

    case 'v1os':
      headers = [
        { field: 'name', name: labels.os },
        { field: 'pv', name: labels.pv },
        { field: 'pv_iframe', name: labels.pvIframe },
        { field: 'uu', name: labels.user },
        { field: 'uu_iframe', name: labels.userIframe },
      ];
      break;

    default:
      console.error('Unsupported collection:', collection);
  }

  return headers;
}
